import {
    Image,
    Avatar,
    useStyles,
    Subheader,
    Carousel,
    pxToRem,
} from '@archipro-design/aria';
import type { ProfessionalBannersData } from '~/modules/professional/page/OverviewPage';
import * as S from './BannerAndLogo.style';
import { useCurrentTabBanner } from '~/modules/professional/hook/use-current-tab-banner-image';
import { BoostResolutionOverlay } from '~/modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import {
    MEDIA_CONIDTION_SIZES_LAPTOP_ONLY,
    MEDIA_CONIDTION_SIZES_LARGE_SCREEN,
} from '~/modules/images/config/sizes-constant';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type SwiperCore from 'swiper';
import BannerAndLogoHeaderProgress from './BannerAndLogoProgress';
import {
    BANNER_CAROUSEL_DELAY,
    BANNER_CAROUSEL_UPDATE_RATE,
} from '~/modules/professional/config/constants';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { useFeature, useMatchesHandleData } from '@modules/root';
import { ProfessionalHeroBannerContent } from '~/modules/professional/component/overview-tab/banner-and-logo/ProfessionalHeroBannerContent';
import { ArrowLeftLine, ArrowRightLine } from '@archipro-design/icons';

let animationTimer = 0;

export const BannerAndLogo: React.FC<{
    banners: ProfessionalBannersData;
    awards?: AwardProps[];
    professional: {
        CompanyLogo: string;
        LogoBackgroundColor: string;
        Title: string;
        Link: string;
        Website: string;
        ID: number;
        Content?: string;
    };
    hideCarouselArrow?: boolean;
    hideLogo?: boolean;
    variant?: 'default' | 'compact';
}> = ({
    banners,
    awards,
    professional,
    hideLogo = false,
    hideCarouselArrow = true,
    variant = 'default',
}) => {
    const {
        imageUrl: bannerImage,
        bannerId,
        wasUpscaled: bannerWasUpscaled,
    } = useCurrentTabBanner(banners);

    const hasLogo = !!professional.CompanyLogo;

    const title = professional.Title;
    const symbolMatch = title.match(/^(.*?)(™|®|©)(.*)$/);

    const filteredName = symbolMatch?.[1]?.trim() || title;
    const filteredSymbol = symbolMatch?.[2] || '';
    const afterSymbol = symbolMatch?.[3]?.trim() || '';

    const enableAwardBadges = useFeature('award_badges');
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const [headerSwiper, setHeaderSwiper] = useState<SwiperCore | null>(null);
    const ref = useRef(null);
    const prevRef = useRef<HTMLButtonElement | null>(null);
    const nextRef = useRef<HTMLButtonElement | null>(null);
    const autoplayPausedRef = useRef(false);
    const bannerHeight = variant === 'compact' ? 680 : 1245;

    const images = useMemo(
        () =>
            banners.map((b, i) => ({
                src: b?.libraryImageFile?.DetailBannerStandard?.Desktop,
                key: i,
                id: b.id,
                wasUpscaled: !!b.wasUpscaled,
            })),
        [banners],
    );

    const delay = BANNER_CAROUSEL_DELAY;
    const updateRate = BANNER_CAROUSEL_UPDATE_RATE;
    const updateBlock = (updateRate / delay) * 100;

    const [currentTile, setCurrentTile] = useState<number>(0);
    const [currentProgress, setCurrentProgress] = useState<number>(0);

    const startAnimationTimer = () => {
        animationTimer = window.setInterval(() => {
            setCurrentProgress((num) => num + updateBlock);
        }, updateRate);
    };

    // Start animate timer
    useEffect(() => {
        if (animationTimer) {
            window.clearInterval(animationTimer);
        }
        setCurrentProgress(0);
        startAnimationTimer();

        return () => {
            window.clearInterval(animationTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delay]);

    useEffect(() => {
        if (!headerSwiper) return;

        const onSlideChange = () => {
            setCurrentProgress(0);
            setCurrentTile(headerSwiper.realIndex);
        };

        headerSwiper.on('slideChange', onSlideChange);
        return () => {
            headerSwiper.off('slideChange', onSlideChange);
        };
    }, [headerSwiper]);

    // Restart Swiper autoplay and progress timer when banner data changes,
    // because Swiper pauses autoplay internally when slides are updated.
    const bannerCountRef = useRef(banners.length);
    useEffect(() => {
        if (!headerSwiper) return;

        const countChanged = bannerCountRef.current !== banners.length;
        bannerCountRef.current = banners.length;

        headerSwiper.update();
        if (!autoplayPausedRef.current) {
            headerSwiper.autoplay?.start();
            window.clearInterval(animationTimer);
            setCurrentProgress(0);
            startAnimationTimer();
        }
        if (countChanged) {
            setCurrentTile(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headerSwiper, banners]);

    const { css } = useStyles({
        hasBannerImage: !!bannerImage,
        hasLogo: hasLogo,
        variant,
    });

    const activeImage = images[currentTile] ?? images[0];

    // Track currentTile in a ref so the stable handleAutoplayPause callback
    // can read the latest value without adding it as a dependency.
    const currentTileRef = useRef(currentTile);
    currentTileRef.current = currentTile;

    // Pin the carousel slide index during boost so the overlay props keep
    // pointing at the image being upscaled, even if the carousel advances.
    const pinnedSlideRef = useRef<number | null>(null);

    const handleAutoplayPause = useCallback(
        (paused: boolean) => {
            autoplayPausedRef.current = paused;
            if (paused) {
                if (pinnedSlideRef.current === null) {
                    pinnedSlideRef.current = currentTileRef.current;
                }
            } else {
                pinnedSlideRef.current = null;
            }
            if (!headerSwiper) return;
            if (paused) {
                headerSwiper.autoplay?.stop();
                window.clearInterval(animationTimer);
            } else {
                headerSwiper.autoplay?.start();
                setCurrentProgress(0);
                window.clearInterval(animationTimer);
                startAnimationTimer();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [headerSwiper],
    );

    // When pinned (boost active), resolve the image from the pinned index so
    // props stay correct even after revalidation or carousel remount.
    const overlayImage =
        pinnedSlideRef.current !== null
            ? (images[pinnedSlideRef.current] ?? activeImage)
            : activeImage;

    const renderBannerSection = () => {
        if (images.length > 1) {
            return (
                <BoostResolutionOverlay
                    libraryImageId={overlayImage?.id ?? 0}
                    currentImageUrl={overlayImage?.src ?? ''}
                    displayWidth={1920}
                    displayHeight={bannerHeight}
                    wasUpscaled={overlayImage?.wasUpscaled ?? false}
                    className="z-[2] h-full"
                    buttonClassName="!bottom-[5rem] !right-[3.125rem]"
                    beforeClassName="top-30"
                    afterClassName="top-30"
                    actionsClassName="!bottom-[5rem]"
                    onAutoplayPause={handleAutoplayPause}
                    disabled={isBMPreview}
                    tooltipPosition="top"
                >
                    <div className={css(S.BannerWrap)}>
                        <Carousel
                            key={images.length}
                            getSwiperApi={(swiper) => {
                                setHeaderSwiper(swiper as SwiperCore);
                            }}
                            ref={ref}
                            aria-roledescription="carousel"
                            items={images.map((img, key) => {
                                if (!img.src) return null;
                                return (
                                    <Image
                                        key={`bannerImgCarousel${key}`}
                                        src={img.src}
                                        width="100%"
                                        height={
                                            variant === 'compact' ? 819 : '100%'
                                        }
                                        layout="fill"
                                        alt={
                                            key === 0
                                                ? `Hero Banner for ${professional.Title}`
                                                : `Supporting Image ${key} for ${professional.Title}`
                                        }
                                        objectFit="cover"
                                        loading={
                                            img.key === 0 ? 'eager' : 'lazy'
                                        }
                                        sizes="100vw"
                                    />
                                );
                            })}
                            autoplay={{
                                disableOnInteraction: false,
                                delay,
                            }}
                            navigation={true}
                            loop={true}
                            effect="fade"
                            fadeEffect={{ crossFade: true }}
                            hidePaddles={true}
                            paddleNext={{ ref: nextRef }}
                            paddlePrevious={{ ref: prevRef }}
                            variables={{
                                overlayPadding: pxToRem(0),
                                overlayInitialOpacity: 1,
                                containerWidth: '100%',
                                containerHeight: pxToRem(bannerHeight),
                            }}
                        />
                        <BannerAndLogoHeaderProgress
                            progress={currentProgress}
                            total={images.length}
                            current={currentTile}
                        />
                        {!hideCarouselArrow && (
                            <>
                                <div
                                    onClick={() => headerSwiper?.slidePrev()}
                                    className={css(S.PaddleBase, S.LeftPaddle)}
                                    style={{ opacity: 0 }}
                                >
                                    <div>
                                        <ArrowLeftLine />
                                    </div>
                                </div>
                                <div
                                    onClick={() => headerSwiper?.slideNext()}
                                    className={css(S.PaddleBase, S.RightPaddle)}
                                >
                                    <div>
                                        <ArrowRightLine />
                                    </div>
                                </div>
                            </>
                        )}
                        {enableAwardBadges && awards && awards.length > 0 && (
                            <BannerAwards
                                awards={awards}
                                heading="Award winning professional"
                                professionalUrl={professional.Link}
                            />
                        )}
                    </div>
                </BoostResolutionOverlay>
            );
        }

        if (bannerImage !== null && bannerId !== null) {
            return (
                <BoostResolutionOverlay
                    libraryImageId={bannerId}
                    currentImageUrl={bannerImage}
                    displayWidth={1920}
                    displayHeight={bannerHeight}
                    wasUpscaled={bannerWasUpscaled}
                    className="z-[2] h-full"
                    buttonClassName="!bottom-[5rem] !right-[3.125rem]"
                    beforeClassName="top-30"
                    afterClassName="top-30"
                    actionsClassName="!bottom-[5rem]"
                    disabled={isBMPreview}
                    tooltipPosition="top"
                >
                    <div className={css(S.BannerWrap)}>
                        <Image
                            src={bannerImage}
                            width="100%"
                            height={bannerHeight}
                            layout="fill"
                            objectFit="cover"
                            loading="eager"
                            alt={`Hero Banner for ${professional.Title}`}
                            sizes={`${MEDIA_CONIDTION_SIZES_LARGE_SCREEN} 1920px,${MEDIA_CONIDTION_SIZES_LAPTOP_ONLY} 95vw, 100vw`}
                        />
                        {awards && awards.length > 0 && (
                            <BannerAwards awards={awards} />
                        )}
                    </div>
                </BoostResolutionOverlay>
            );
        }

        return (
            <div className={css(S.BannerWrap)}>
                <Subheader variant="03">Banner image</Subheader>
            </div>
        );
    };

    return (
        <div className={css(S.Container)}>
            {renderBannerSection()}

            <ProfessionalHeroBannerContent
                professional={{
                    ...professional,
                    filteredName,
                    filteredSymbol,
                    afterSymbol,
                    hasSymbol: !!filteredSymbol,
                }}
                variant={variant}
            />

            {!hideLogo && (
                <div className={css(S.LogoWrap)}>
                    <Avatar
                        image={{
                            src: professional.CompanyLogo ?? '',
                            alt: `${professional.Title} company logo`,
                        }}
                        initials="Logo"
                        size={214}
                        shape="square"
                        imageFit="contain"
                        styles={{ borderWidth: 0 }}
                        variables={{
                            avatarImageBackgroundColor:
                                professional.LogoBackgroundColor ?? 'white',
                            borderRadius: '0',
                        }}
                    />
                </div>
            )}
        </div>
    );
};
